import React, { useRef, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { useDarkMode } from "../contexts/DarkModeContext";
import { describeSource } from "./previewUtils";

// Inline preview for video sources (.mp4, .webm, …). Unlike DOCX we don't
// fetch-then-blob: video files are large and a blob URL kills HTTP range
// requests (seeking/streaming). Instead we point the <video> element straight
// at the resolved URL — a signed cloud URL plays cross-origin without CORS,
// and the same-origin Discover proxy sends cookies automatically. Bearer-only
// auth can't ride a media element, so we surface a Download / Open CTA as the
// fallback path.
//
// `startTime` (seconds) seeks to the cited moment when the chunk carries a
// timestamp; omitted otherwise.
export default function VideoPreviewModal({ docName, videoUrl, fullDocUrl, downloadUrl, source, provider, startTime, onClose }) {
  const { isDarkMode } = useDarkMode();
  const videoRef = useRef(null);

  const handleLoadedMetadata = useCallback(() => {
    if (startTime && videoRef.current && Number.isFinite(startTime)) {
      try { videoRef.current.currentTime = startTime; } catch { /* ignore */ }
    }
  }, [startTime]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === "Escape") onClose();
  }, [onClose]);


  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  const sourceLabel = describeSource(source, provider);

  return (
    <>
      <div
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9998] transition-opacity"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Preview of ${docName}`}
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      >
        <div
          className={`relative w-full max-w-4xl max-h-[90vh] rounded-xl shadow-2xl flex flex-col ${
            isDarkMode ? "bg-slate-800" : "bg-white"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={`flex items-center justify-between px-5 py-3 border-b ${
            isDarkMode ? "border-slate-700" : "border-gray-200"
          }`}>
            <div className="flex-1 mr-4 min-w-0">
              <h3 className={`text-sm font-semibold truncate ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}>{docName}</h3>
              <div className={`text-xs mt-0.5 truncate ${
                isDarkMode ? "text-slate-400" : "text-gray-500"
              }`}>
                Inline preview &middot; full video hosted on {sourceLabel}
              </div>
              {(fullDocUrl || downloadUrl) && (
                <a
                  href={fullDocUrl || downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={fullDocUrl || downloadUrl}
                  className={`block text-[11px] mt-0.5 truncate font-mono underline ${
                    isDarkMode ? "text-sky-400 hover:text-sky-300" : "text-sky-600 hover:text-sky-700"
                  }`}
                >
                  {fullDocUrl || downloadUrl}
                </a>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className={`p-1 rounded-lg transition-colors ${
                isDarkMode
                  ? "hover:bg-slate-700 text-slate-400 hover:text-slate-200"
                  : "hover:bg-gray-100 text-gray-500 hover:text-gray-700"
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="overflow-auto p-4 flex-1 min-h-[200px] flex items-center justify-center bg-black">
            <video
              ref={videoRef}
              src={videoUrl}
              controls
              autoPlay
              playsInline
              onLoadedMetadata={handleLoadedMetadata}
              className="max-w-full max-h-[75vh] rounded-md"
            >
              <track kind="captions" />
            </video>
          </div>
        </div>
      </div>
    </>
  );
}

VideoPreviewModal.propTypes = {
  docName: PropTypes.string.isRequired,
  videoUrl: PropTypes.string.isRequired,
  fullDocUrl: PropTypes.string,
  downloadUrl: PropTypes.string,
  source: PropTypes.oneOf(["connector", "local", "proxy"]).isRequired,
  provider: PropTypes.string,
  startTime: PropTypes.number,
  onClose: PropTypes.func.isRequired,
};
